import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, KeyRound, ShieldCheck, Download, Info, Fingerprint } from "lucide-react";
import { api } from "../lib/api";
import { loginWithPasskey, passkeyPlatformAvailable } from "../lib/passkey";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import logo from "../assets/workzone-logo.png";

export default function EmployeeLanding() {
  const { user, setUser, checkAuth } = useAuth();
  const { t, dir } = useLang();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [bioSupported, setBioSupported] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [brand, setBrand] = useState(null);
  const [logoOk, setLogoOk] = useState(true);
  // Resolve the employee's company: prefer the ?org= link, else the last company saved on this device.
  const [orgId, setOrgId] = useState(() =>
    new URLSearchParams(window.location.search).get("org") || localStorage.getItem("emp_portal_org") || "");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const tab = p.get("tab");
    if (tab) sessionStorage.setItem("portal_initial_tab", tab);
    const org = p.get("org") || localStorage.getItem("emp_portal_org") || "";
    if (org) {
      if (p.get("org")) localStorage.setItem("emp_portal_org", p.get("org"));
      setOrgId(org);
      api.publicOrgBranding(org).then(setBrand).catch(() => {});
    }
  }, []);

  useEffect(() => { passkeyPlatformAvailable().then(setBioSupported); }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const i = p.get("i"); const pw = p.get("p");
    if (!i && !pw) return;
    if (i) setIdentifier(i);
    if (pw) setPassword(pw);
    // strip credentials from the URL/history immediately
    window.history.replaceState({}, "", "/employee");
    if (i && pw) autoLogin(i, pw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link ? link.getAttribute("href") : null;
    if (link) link.setAttribute("href", "/manifest-employee.json");
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => {
      if (link && prev) link.setAttribute("href", prev);
      window.removeEventListener("beforeinstallprompt", onPrompt);
    };
  }, []);

  const doInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    try { await installPrompt.userChoice; } catch (_) {}
    setInstallPrompt(null);
  };

  const autoLogin = async (id, pw) => {
    setBusy(true);
    try {
      const u = await api.portalLogin(String(id).trim(), pw, orgId);
      if (u?.org_id) localStorage.setItem("emp_portal_org", u.org_id);
      setUser({ kind: "employee", ...u });
      toast.success(`${t("portal.login.welcome")} ${u.name || ""}`);
      await checkAuth();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("portal.login.autoFail"));
    } finally { setBusy(false); }
  };

  useEffect(() => {
    const id = identifier.trim();
    if (!/^1\d{9}$/.test(id) || busy || password) return;
    let alive = true;
    api.portalLogin(id, "", orgId).then(async (u) => {
      if (!alive) return;
      if (u?.org_id) localStorage.setItem("emp_portal_org", u.org_id);
      setUser({ kind: "employee", ...u });
      toast.success(`${t("portal.login.welcome")} ${u.name || ""}`);
      await checkAuth();
      navigate("/", { replace: true });
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier]);

  if (user?.kind === "employee") return <Navigate to="/" replace />;

  const doLogin = async () => {
    if (!identifier.trim()) { toast.warning(t("portal.login.needId")); return; }
    setBusy(true);
    try {
      const u = await api.portalLogin(identifier.trim(), password, orgId);
      if (u?.org_id) localStorage.setItem("emp_portal_org", u.org_id);
      setUser({ kind: "employee", ...u });
      toast.success(`${t("portal.login.welcome")} ${u.name || ""}`);
      await checkAuth();
      navigate("/", { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || t("portal.login.loginFail")); }
    finally { setBusy(false); }
  };

  const doPasskeyLogin = async () => {
    if (!identifier.trim()) { toast.warning(t("portal.login.needIdFirst")); return; }
    setBioBusy(true);
    try {
      const u = await loginWithPasskey(identifier.trim(), "employee");
      if (u?.org_id) localStorage.setItem("emp_portal_org", u.org_id);
      setUser({ kind: "employee", ...u });
      toast.success(`${t("portal.login.welcome")} ${u.name || ""}`);
      await checkAuth();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? t("portal.login.passkeyCancel")
        : (e?.response?.data?.detail || t("portal.login.passkeyFail")));
    } finally { setBioBusy(false); }
  };

  const inputCls = "bg-white/95 border-slate-200 text-slate-900 placeholder:text-slate-400 h-11";

  return (
    <div dir={dir} data-testid="employee-landing"
      className="relative min-h-screen flex items-center justify-center px-4 py-10 font-body overflow-hidden bg-slate-950">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 right-1/4 h-96 w-96 rounded-full bg-blue-600/25 blur-[120px]" />
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-emerald-500/15 blur-[120px]" />
      </div>

      <div className="absolute top-4 left-4 z-10">
        <LanguageSwitcher tone="dark" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="h-20 w-20 overflow-hidden rounded-2xl bg-white shadow-lg shadow-blue-600/25 ring-1 ring-white/20">
            <img
              src={orgId && brand?.has_logo && logoOk ? api.publicOrgLogoUrl(orgId) : logo}
              onError={() => setLogoOk(false)}
              alt={brand?.name || "WorkZone"} className="h-full w-full object-contain p-1.5" />
          </div>
          <h1 className="mt-3 font-heading text-3xl font-extrabold text-white tracking-tight">{brand?.name || "WorkZone"}</h1>
          <p className="mt-1 text-sm text-slate-400 flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-emerald-400" /> {t("portal.login.portalBadge")}
          </p>
        </div>

        <div className="rounded-3xl border border-white/10 bg-slate-800/60 backdrop-blur-xl p-6 shadow-2xl">
          <div className="mb-5 text-center">
            <h2 className="font-heading text-lg font-bold text-white">{t("portal.login.title")}</h2>
            <p className="mt-1 text-xs text-slate-400">{t("portal.login.hint")}</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300">{t("portal.login.idLabel")}</Label>
              <Input data-testid="emp-identifier-input" dir="ltr" inputMode="email" autoCapitalize="none" placeholder={t("portal.login.idPh")}
                className={inputCls} value={identifier}
                onChange={(e) => setIdentifier(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doLogin()} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">{t("portal.login.pwLabel")} <span className="text-slate-500">{t("portal.login.pwHint")}</span></Label>
              <Input data-testid="emp-password-input" type="password" className={inputCls} value={password}
                onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doLogin()} />
            </div>
            <Button data-testid="emp-login-btn" disabled={busy} onClick={doLogin}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 gap-1.5 text-base">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} {t("portal.login.loginBtn")}
            </Button>
            {bioSupported && (
              <Button data-testid="emp-passkey-btn" disabled={bioBusy} onClick={doPasskeyLogin}
                className="w-full h-11 bg-white/10 hover:bg-white/20 border border-white/15 text-white gap-1.5 text-base">
                {bioBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} {t("portal.login.passkeyBtn")}
              </Button>
            )}

            <div className="flex items-start gap-2 rounded-xl bg-white/5 border border-white/10 p-3 text-xs text-slate-400" data-testid="emp-forgot-note">
              <Info className="h-4 w-4 shrink-0 text-slate-400 mt-0.5" />
              <span>{t("portal.login.forgotNote")}</span>
            </div>
          </div>
        </div>

        {installPrompt && (
          <div className="mt-5 flex justify-center">
            <button data-testid="emp-install-pwa" onClick={doInstall}
              className="flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-slate-200 hover:bg-white/20 transition-colors">
              <Download className="h-4 w-4" /> {t("portal.login.installBtn")}
            </button>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate-500">
          {t("portal.login.footer")}
        </p>
      </div>
    </div>
  );
}
